import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useCallback } from 'react';
import { Info } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { AdditionalFunctionality } from '@/react/portainer/settings/types';
import { useUpdateAdditionalFunctionalitySettingsMutation } from '@/react/portainer/settings/queries';

import { LoadingButton } from '@@/buttons/LoadingButton';
import { TextTip } from '@@/Tip/TextTip/TextTip';

import { EnableObservabilitySection } from './Observability/EnableObservabilitySection';

interface FormValues {
  Observability: boolean;
}

const validation = yup.object({
  Observability: yup.boolean(),
});

interface Props {
  settings: AdditionalFunctionality;
}

export function AdditionalFunctionalityForm({ settings }: Props) {
  const initialValues: FormValues = settings;
  const updateSettingsMutation =
    useUpdateAdditionalFunctionalitySettingsMutation();

  const handleSubmit = useCallback(
    ({ Observability }: FormValues) => {
      updateSettingsMutation.mutate(
        {
          Observability,
        },
        {
          onSuccess() {
            notifySuccess(
              'Success',
              'Successfully updated additional functionality settings'
            );
          },
        }
      );
    },
    [updateSettingsMutation]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation}
      validateOnMount
      enableReinitialize
    >
      {({ isValid, dirty }) => (
        <Form className="form-horizontal">
          <TextTip color="blue" icon={Info}>
            Additional functionality refers to future enhancements and features
            that will extend the capabilities beyond the current offering.
          </TextTip>
          <div className="form-group col-sm-12">
            <EnableObservabilitySection />
          </div>

          <div className="form-group mt-8">
            <div className="col-sm-12">
              <LoadingButton
                loadingText="Saving settings..."
                isLoading={updateSettingsMutation.isLoading}
                disabled={!isValid || !dirty}
                className="!ml-0"
                data-cy="settings-additionalButton"
              >
                Save additional functionality settings
              </LoadingButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
